<template>
  <section>
    <EditOutlined class="icon" @click="handleShowModal" />
    <a-drawer
      v-model:visible="visible"
      title="Editar información"
      destroy-on-close
      :mask-closable="false"
      placement="right"
      width="480"
      keyboard
      :body-style="{ padding: 0 }"
      @close="handleCloseDrawer"
    >
      <custom-scrollbar class="modal__body" height="calc(100vh - 108px)">
        <a-form
          v-if="customData"
          class="form"
          layout="vertical"
          ref="formRef"
          :model="formState"
          @submit.prevent="handleSubmit"
        >
          <div v-for="item in columns" :key="item.index">
            <a-form-item :label="item.title" :name="item.title">
              <a-select
                v-if="isSelectField(item)"
                :value="selectFieldValues[item.dataIndex]"
                :getPopupContainer="(triggerNode) => triggerNode.parentNode"
                class="form__select"
                show-search
                :filter-option="false"
                :options="selectFieldsState[item.dataIndex]?.options || []"
                :loading="selectFieldsState[item.dataIndex]?.loading"
                placeholder="Seleccionar"
                @change="handleSelectChange(item, $event)"
                @search="handleSelectSearch(item, $event)"
                @popupScroll="handleSelectScroll(item, $event)"
              >
                <template #notFoundContent>
                  <div class="form__empty-options">
                    <SearchOutlined class="form__empty-options-icon" />
                    <span>No se encontraron resultados a tu búsqueda</span>
                  </div>
                </template>
              </a-select>
              <a-input
                v-else
                v-model:value="formState[item.dataIndex]"
                :defaultValue="customData[item.dataIndex]"
              />
            </a-form-item>
          </div>
        </a-form>
      </custom-scrollbar>

      <div class="footer">
        <a-button class="footer__button" @click="handleCloseDrawer"> Cancelar </a-button>
        <a-button type="primary" @click="handleSubmit"> Guardar cambios </a-button>
      </div>
    </a-drawer>
  </section>
</template>

<script>
import CustomScrollbar from "@/app/shared/components/organisms/CustomScrollbar.vue";
import { defineComponent, ref, reactive, toRaw } from "vue";
import { EditOutlined, SearchOutlined } from "@ant-design/icons-vue";
import EventBus from "@/app/shared/utils/eventBus";
import httpManager from "@/app/shared/utils/httpManager";

export default defineComponent({
  components: {
    CustomScrollbar,
    EditOutlined,
    SearchOutlined,
  },
  setup() {
    const formRef = ref();
    const formState = reactive({});

    return {
      formState,
      formRef,
    };
  },
  props: {
    record: {
      type: Object,
      required: true,
    },
    itemId: {
      type: Number || String,
      required: true,
    },
  },
  data() {
    return {
      visible: false,
      customFields: {},
      selectFieldsState: {},
      selectFieldValues: {},
      selectSearchTimer: null,
    };
  },
  computed: {
    getColumns() {
      return this.$store.getters["getContactCustomFields"];
    },
    customData() {
      return this.$store.getters["getContactCustomData"];
    },
    columns() {
      let cols = this.getColumns;
      cols = cols.filter((col) => col.title != "id" && col.title != "Acciones");
      return cols;
    },
  },

  watch: {
    visible(value) {
      if (value) {
        this.selectFieldsState = {};
        this.selectFieldValues = {};
        Object.keys(this.formState).forEach((key) => delete this.formState[key]);

        this.columns.forEach((column) => {
          const fieldValue = this.customData[column.dataIndex];

          if (this.isSelectField(column)) {
            const options = column.options || [];
            this.selectFieldValues[column.dataIndex] =
              fieldValue == null ? undefined : String(fieldValue);
            this.selectFieldsState[column.dataIndex] = {
              options: this.toSelectOptions(options),
              offset: options.length,
              search: "",
              hasMore: options.length < column.options_count,
              loading: false,
              requestId: 0,
            };
          }
        });
      }
    },
  },

  beforeUnmount() {
    clearTimeout(this.selectSearchTimer);
  },

  methods: {
    isSelectField(field) {
      return field?.field_type === "select";
    },
    toSelectOptions(values) {
      return values.map((option) => {
        const value = typeof option === "object" ? option.value : option;
        return { label: value, value: String(value) };
      });
    },
    handleSelectChange(field, value) {
      this.selectFieldValues[field.dataIndex] = value;
      this.formState[field.dataIndex] = value;
    },
    handleSelectSearch(field, value) {
      clearTimeout(this.selectSearchTimer);
      this.selectSearchTimer = setTimeout(
        () => this.fetchSelectOptions(field, value.trim(), true),
        300,
      );
    },
    handleSelectScroll(field, event) {
      const target = event.target;
      const isNearBottom = target.scrollTop + target.clientHeight >= target.scrollHeight - 24;

      if (isNearBottom) this.fetchSelectOptions(field);
    },
    async fetchSelectOptions(field, search, reset = false) {
      const state = this.selectFieldsState[field.dataIndex];
      if (!state || !field.options_url || (!reset && (state.loading || !state.hasMore))) return;

      const requestedSearch = reset ? search : state.search;
      const offset = reset ? 0 : state.offset;
      const requestId = state.requestId + 1;
      state.requestId = requestId;
      state.loading = true;

      try {
        const separator = field.options_url.includes("?") ? "&" : "?";
        const query = new URLSearchParams({
          limit: "50",
          offset: String(offset),
        });

        if (requestedSearch) query.set("search", requestedSearch);

        const response = await httpManager.get(`${field.options_url}${separator}${query}`);
        if (state.requestId !== requestId) return;

        const options = this.toSelectOptions(response.data.results || []);
        state.options = reset ? options : [...state.options, ...options];
        state.offset = offset + options.length;
        state.search = requestedSearch;
        state.hasMore = Boolean(response?.data?.next);
      } catch {
        if (state.requestId === requestId) {
          this.$message.error("No se pudieron cargar las opciones del campo.");
        }
      } finally {
        if (state.requestId === requestId) state.loading = false;
      }
    },
    handleDataCustomChange($event, key) {
      this.customFields[key] = $event;
    },
    handleShowModal() {
      this.visible = true;
    },
    handleCloseDrawer() {
      this.visible = false;
    },
    handleSubmit() {
      this.formRef.validateFields().then(async () => {
        try {
          await this.$store.dispatch("updateInformationCustom", {
            contactId: this.itemId,
            body: toRaw(this.formState),
          });
          this.$store.dispatch("fetchContactCustomData", this.itemId);
          this.handleCloseDrawer();
          this.$message.success("Se ha editado información correctamente");
          EventBus.emit("UPDATE_DATA_CONTACT");
          this.$emit("update");
        } catch (error) {
          this.$message.error("Se ha producido un error al editar la información");
        }
      });
    },
  },
});
</script>

<style lang="sass" scoped>
.modal
  &__body
    padding: 12px 24px

.footer
  display: flex
  align-items: center
  justify-content: flex-end
  padding: 10px 16px
  border-top: $border-normal
  position: absolute
  width: 100%
  bottom: 0
  left: 0

  &__button
    margin-right: 12px

:deep(.ant-input-number)
  width: 100%

:deep(.ant-calendar-picker)
  width: 100%

.icon
  color: $blue-6
  margin-left: $margin-tiny
  margin-top: 3px

.form__select
  width: 100%

.form
  &__empty-options
    display: flex
    min-height: 100px
    flex-direction: column
    align-items: center
    justify-content: center
    gap: 8px
    text-align: center

  &__empty-options-icon
    font-size: 40px
</style>
